import { useEffect, type ReactNode } from 'react';
import { Box } from '../primitives/Box.js';
import { Stack } from '../primitives/Stack.js';
import { tokens } from '../tokens.js';
import { cx } from '../utils/cx.js';
import styles from './PageRouteScaffold.module.css';

/**
 * Hamssah admin page chrome contract (audit scorecard 2026-06):
 *
 * | Route            | band | variant="dock" | viewportLock |
 * |------------------|------|----------------|--------------|
 * | /                | yes  | yes            | no           |
 * | /search          | yes  | yes            | no           |
 * | /catalog         | yes  | yes            | yes (products)|
 * | /catalog/products/:id | yes | yes     | no           |
 * | /orders          | yes  | yes            | no           |
 * | /inventory       | yes  | yes            | no           |
 * | /procurement/*   | yes  | yes            | no           |
 * | /users           | yes  | yes            | no           |
 * | /settings/*      | yes  | yes            | no           |
 *
 * Layer scores (0–3): foundation 2 | shell 2 | data 2 | forms 2 | delight 1 | a11y 2
 * P0 fixed: product deep-links, Flows→Parcours label, dock header parity on top routes.
 * P1 open: table loading overlay, tiered empties, viewport pageSize smoke on Produits.
 */
export interface PageRouteScaffoldProps {
  band?: ReactNode;
  bandEnd?: ReactNode;
  children: ReactNode;
  /** Lock page to viewport height — no document vertical scroll; children must flex + paginate. */
  viewportLock?: boolean;
  /**
   * Edge-to-edge: drop scaffold gutters and zero PageShell `<main>` inset
   * (`data-wp-page-flush`). Child owns its own padding.
   */
  flush?: boolean;
}

export function PageRouteScaffold({ band, bandEnd, children, viewportLock = false }: PageRouteScaffoldProps) {
  useEffect(() => {
    if (!viewportLock || typeof document === 'undefined') return;
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
    };
  }, [viewportLock]);

  return (
    <Stack
      gap="md"
      className={viewportLock ? styles.viewportLock : undefined}
      style={{
        flex: 1,
        minHeight: 0,
        paddingLeft: tokens.page.gutterX,
        paddingRight: tokens.page.gutterX,
        paddingTop: 'var(--wp-space-16)',
        paddingBottom: 'var(--wp-space-16)',
      }}
    >
      {band ? (
        <Box className={viewportLock ? styles.viewportLockBand : undefined}>
          <Stack gap="xs">
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              {band}
              {bandEnd}
            </Box>
          </Stack>
        </Box>
      ) : null}
      <div
        className={cx(styles.contentMax, viewportLock && styles.viewportLockBody)}
        {...(viewportLock ? { 'data-wp-viewport-lock-body': '' } : {})}
      >
        {children}
      </div>
    </Stack>
  );
}
